import filter from 'lodash/filter';
import findIndex from 'lodash/findIndex';
import findLastIndex from 'lodash/findLastIndex';
import lodashGet from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isFunction from 'lodash/isFunction';
import map from 'lodash/map';
import some from 'lodash/some';
import { StateCreator } from 'zustand';

import { defaultOverrideFunction } from './functions/defaultOverrideFunction';
import { RouteOverrideFunction } from './types/RouteOverrideFunction';
import { WorkflowPage } from './types/WorkflowPage';
import { WorkflowState } from './types/WorkflowState';
import { applicantWorkflow } from './workflows/applicant';
import { applyWorkflow } from './workflows/apply';

export const workflowStore: StateCreator<WorkflowState> = (set, get) => ({
  activeWorkflow: null,
  availableWorkflows: [applyWorkflow, applicantWorkflow],
  currentRoute: '',

  computed: {
    get activeWorkflowChecksCompletion(): boolean {
      return some(get().activeWorkflow, ({ completed }): boolean =>
        isFunction(completed),
      );
    },
    get completedPages(): WorkflowPage[] {
      const { activeWorkflow } = get();

      return filter(activeWorkflow, (page: WorkflowPage): boolean =>
        page.completed ? page.completed() : false,
      );
    },
    get currentPageIndex(): number {
      const { activeWorkflow, currentRoute } = get();

      if (!activeWorkflow || !currentRoute) {
        return -1;
      }

      const currentIndex = findIndex(
        activeWorkflow,
        ({ path }): boolean => path === currentRoute,
      );

      return currentIndex;
    },
    get hasCompletedPages(): boolean {
      const {
        computed: { completedPages },
      } = get();

      return !isEmpty(completedPages);
    },
    get hasIncompletePagesBeforeCurrent(): boolean {
      const {
        computed: { currentPageIndex, incompleteIndexes },
      } = get();

      return some(
        incompleteIndexes,
        (index: number): boolean => index < currentPageIndex,
      );
    },
    get incompleteIndexes(): number[] {
      return filter(
        map(
          get().activeWorkflow,
          (page: WorkflowPage, index: number): number => {
            if (page.completed) {
              return page.completed() ? -1 : index;
            }

            return -1;
          },
        ),
        (index: number): boolean => index !== -1,
      );
    },
    get lastPageIndex(): number {
      const { activeWorkflow } = get();

      return activeWorkflow ? activeWorkflow.length - 1 : -1;
    },
    get nextIncompleteIndex(): number {
      const {
        activeWorkflow,
        computed: { currentPageIndex },
      } = get();

      if (isEmpty(activeWorkflow)) {
        return -1;
      }

      return findIndex(
        activeWorkflow,
        (page: WorkflowPage): boolean =>
          page.completed ? !page.completed() : false,
        currentPageIndex,
      );
    },
    get nextPageIndex(): number {
      const {
        activeWorkflow,
        computed: { currentPageIndex },
      } = get();

      // if there is no active workflow just return zero
      // as we do not have an array to traverse
      if (!activeWorkflow) {
        return 0;
      }

      const nextPageIndex = Math.min(
        currentPageIndex + 1,
        activeWorkflow.length,
      );

      return findIndex(
        activeWorkflow,
        (page: WorkflowPage): boolean =>
          page.condition ? page.condition() : true,
        nextPageIndex,
      );
    },
    get previousPageIndex(): number {
      const {
        activeWorkflow,
        computed: { currentPageIndex },
      } = get();

      // if there is no active workflow just return zero
      // as we do not have an array to traverse
      if (!activeWorkflow) {
        return 0;
      }

      const previousPageIndex = Math.max(currentPageIndex - 1, 0);

      return findLastIndex(
        activeWorkflow,
        (page: WorkflowPage): boolean =>
          page.condition ? page.condition() : true,
        previousPageIndex,
      );
    },
    get progress(): number {
      const {
        activeWorkflow,
        computed: { currentPageIndex },
      } = get();

      if (!activeWorkflow || currentPageIndex === -1) {
        return 0;
      }

      return Math.round(((currentPageIndex + 1) / activeWorkflow.length) * 100);
    },
  },

  detect(
    currentRoute: string,
    setRouteOverride: RouteOverrideFunction = defaultOverrideFunction,
  ): void {
    let route = currentRoute;

    // clean up ending forward slash to better match route path
    if (route.endsWith('/')) {
      route = route.substring(0, route.length - 1);
    }

    set({ currentRoute: route });

    const { workflowByPath } = get();

    // we are making an assumption that there is a 1:1 mapping
    // between the route and the workflow, meaning we can just
    // use the first workflow that matches the current route
    const activeWorkflow = workflowByPath(route);

    set({ activeWorkflow });

    if (activeWorkflow) {
      // important to get these values after current route and active workflow
      // have been set, otherwise there will be some unexpected behavior
      const {
        computed: { hasIncompletePagesBeforeCurrent },
        first,
      } = get();

      if (hasIncompletePagesBeforeCurrent) {
        setRouteOverride(first());
      }
    }
  },
  first(): string {
    const { activeWorkflow, currentRoute } = get();

    return lodashGet(activeWorkflow, [0, 'path']) || currentRoute;
  },
  last(): string {
    const {
      activeWorkflow,
      computed: { lastPageIndex },
      currentRoute,
    } = get();

    return lodashGet(activeWorkflow, [lastPageIndex, 'path']) || currentRoute;
  },
  next(): string {
    const {
      activeWorkflow,
      computed: { nextPageIndex },
      currentRoute,
    } = get();

    return lodashGet(activeWorkflow, [nextPageIndex, 'path']) || currentRoute;
  },
  previous(): string {
    const {
      activeWorkflow,
      computed: { previousPageIndex },
      currentRoute,
    } = get();

    return (
      lodashGet(activeWorkflow, [previousPageIndex, 'path']) || currentRoute
    );
  },
  progressByPath: (providedPath: string | RegExp): number => {
    const { workflowByPath } = get();

    const workflow = workflowByPath(providedPath);

    const index = findIndex(workflow, ({ path }): boolean =>
      providedPath instanceof RegExp
        ? path.match(providedPath) !== null
        : path === providedPath,
    );

    if (index === -1) {
      return 0;
    }

    return Math.round(((index + 1) / workflow.length) * 100);
  },
  resume(): string {
    const {
      activeWorkflow,
      computed: {
        activeWorkflowChecksCompletion,
        currentPageIndex,
        nextIncompleteIndex,
        incompleteIndexes,
      },
      currentRoute,
      last,
      next,
    } = get();

    // if there is not an active workflow
    // or the current route is not in the active workflow
    // return the current route
    if (currentPageIndex === -1) {
      return currentRoute;
    }

    // the current route is in the active workflow
    // and we have an active workflow
    // and no incomplete pages
    if (isEmpty(incompleteIndexes)) {
      // and the active workflow checks completion of pages
      // return the last page of the workflow
      if (activeWorkflowChecksCompletion) {
        return last();
      }

      // and the active workflow does not check the completion of pages
      // return the next page of the workflow
      return next();
    }

    // the current route is in the active workflow
    // and we have an active workflow
    // and we have incomplete pages
    // but the next incomplete index is likely prior to the current index
    // return the first incomplete page of the workflow
    if (nextIncompleteIndex === -1) {
      return activeWorkflow?.[incompleteIndexes[0]]?.path || currentRoute;
    }

    // the current route is in the active workflow
    // and we have an active workflow
    // and we have an incomplete page after the current page
    // return the next incomplete page
    // fallback to the current route if the path is falsy (null, '', etc)
    return (
      lodashGet(activeWorkflow, [nextIncompleteIndex, 'path']) || currentRoute
    );
  },
  workflowByPath: (providedPath: string | RegExp): WorkflowPage[] => {
    const { availableWorkflows } = get();

    return (
      availableWorkflows.find(
        (workflow: WorkflowPage[]): WorkflowPage =>
          workflow.find(({ path }): boolean =>
            providedPath instanceof RegExp
              ? path.match(providedPath) !== null
              : path === providedPath,
          ),
      ) || null
    );
  },
});
